import { useState, useEffect } from 'react'
import { db, Investment, Portfolio, Transaction } from '../lib/supabase'

export function useInvestments(investorId: string | null) {
  const [investments, setInvestments] = useState<any[]>([])
  const [portfolio, setPortfolio] = useState<Portfolio | null>(null)
  const [transactions, setTransactions] = useState<Transaction[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    
    if (!investorId) {
      setLoading(false)
      return
    }

    loadInvestmentData()
  }, [investorId])

  const loadInvestmentData = async () => {
    if (!investorId) return

    try {
      setLoading(true)
      
      // Load all investment-related data in parallel
      const [investmentsData, portfolioData, transactionsData] = await Promise.all([
        db.getInvestments(investorId),
        db.getPortfolio(investorId).catch(() => null), // Portfolio might not exist
        db.getTransactions(investorId)
      ])

      setInvestments(investmentsData || [])
      setPortfolio(portfolioData)
      setTransactions(transactionsData || [])

      setError(null)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error loading investment data')
    } finally {
      setLoading(false)
    }
  }

  const createInvestment = async (investment: Omit<Investment, 'id' | 'created_at' | 'updated_at'>) => {
    try {
      const newInvestment = await db.createInvestment(investment)
      await loadInvestmentData() // Reload all data
      return newInvestment
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error creating investment')
      throw err
    }
  }

  const addTransaction = async (transaction: Omit<Transaction, 'id' | 'created_at'>) => {
    try {
      const newTransaction = await db.createTransaction(transaction)
      await loadInvestmentData() // Reload all data
      return newTransaction
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error adding transaction')
      throw err
    }
  }

  // Calculate derived metrics
  const metrics = {
    totalInvested: portfolio?.total_invested || 0,
    currentValue: portfolio?.current_value || 0,
    totalReturn: portfolio?.total_return || 0,
    returnPercentage: portfolio?.return_percentage || 0,
    activeInvestments: investments.filter(inv => inv.status === 'active').length,
    pendingInvestments: investments.filter(inv => inv.status === 'pending').length,
    completedInvestments: investments.filter(inv => inv.status === 'completed').length,
    recentTransactions: transactions.slice(0, 10),
    monthlyDividends: transactions
      .filter(t => t.type === 'dividend' && 
        new Date(t.transaction_date) > new Date(Date.now() - 30 * 24 * 60 * 60 * 1000))
      .reduce((sum, t) => sum + t.amount, 0)
  }

  return {
    investments,
    portfolio,
    transactions,
    metrics,
    loading,
    error,
    createInvestment,
    addTransaction,
    refresh: loadInvestmentData
  }
}